import { transformPoint, type Matrix } from '../core/geometry';
import type { RasterTileStore } from '../core/tiles';

// Extremely compressed/skewed sources can require a source-space gutter larger
// than a safe scratch canvas. Sample only the destination bounds in that case.
// Interpolation uses premultiplied RGBA, so transparent colors cannot bleed.
export function sampleStoreRegion(store: RasterTileStore, matrix: Matrix, inverse: Matrix,
  targetWidth: number, targetHeight: number, defaultAlpha: 0 | 255, smoothing = true) {
  const corners = [{ x: 0, y: 0 }, { x: store.width, y: 0 }, { x: store.width, y: store.height }, { x: 0, y: store.height }].map(point => transformPoint(matrix, point));
  const x = Math.max(0, Math.floor(Math.min(...corners.map(p => p.x))) - 1);
  const y = Math.max(0, Math.floor(Math.min(...corners.map(p => p.y))) - 1);
  const right = Math.min(targetWidth, Math.ceil(Math.max(...corners.map(p => p.x))) + 1);
  const bottom = Math.min(targetHeight, Math.ceil(Math.max(...corners.map(p => p.y))) + 1);
  if (right <= x || bottom <= y) return null;
  const image = new ImageData(right - x, bottom - y);
  const pixel = (sx: number, sy: number) => {
    sx = Math.max(0, Math.min(store.width - 1, sx)); sy = Math.max(0, Math.min(store.height - 1, sy));
    const tx = Math.floor(sx / store.tileSize); const ty = Math.floor(sy / store.tileSize);
    const bytes = store.getTile(tx, ty);
    if (!bytes) return [defaultAlpha, defaultAlpha, defaultAlpha, defaultAlpha] as const;
    const width = store.tileDimensions(tx, ty).width;
    const offset = ((sy - ty * store.tileSize) * width + sx - tx * store.tileSize) * 4;
    return [bytes[offset]!, bytes[offset + 1]!, bytes[offset + 2]!, bytes[offset + 3]!] as const;
  };
  for (let row = 0; row < image.height; row++) for (let col = 0; col < image.width; col++) {
    const point = transformPoint(inverse, { x: x + col + 0.5, y: y + row + 0.5 });
    if (!smoothing) {
      image.data.set(pixel(Math.floor(point.x), Math.floor(point.y)), (row * image.width + col) * 4);
      continue;
    }
    const sx = point.x - 0.5; const sy = point.y - 0.5;
    const left = Math.floor(sx); const top = Math.floor(sy); const dx = sx - left; const dy = sy - top;
    const values = [pixel(left, top), pixel(left + 1, top), pixel(left, top + 1), pixel(left + 1, top + 1)];
    const weights = [(1 - dx) * (1 - dy), dx * (1 - dy), (1 - dx) * dy, dx * dy];
    let alpha = 0; const rgb = [0, 0, 0];
    for (let i = 0; i < 4; i++) {
      const value = values[i]!; const weight = weights[i]!;
      alpha += value[3] * weight;
      for (let c = 0; c < 3; c++) rgb[c] = rgb[c]! + value[c]! * value[3] * weight;
    }
    const offset = (row * image.width + col) * 4;
    image.data[offset + 3] = alpha;
    for (let c = 0; c < 3; c++) image.data[offset + c] = alpha > 0 ? rgb[c]! / alpha : 0;
  }
  return { x, y, image, corners };
}
